import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  CheckboxGroup,
  Column,
  ColumnProps,
  Icon,
  PageBar,
  Table as SPATable,
  TableProps as SPATableProps,
} from '@tencent/spaui';
import filter from 'images/filter.svg';
import './index.scss';
import Tooltip from 'components/tooltip';

interface FilterProps {
  name: string;
  label: string;
  value: string[];
  data: { value: string; label: string }[];
  onChange: (name: string, value: string[]) => void;
}

const Filter: React.FC<FilterProps> = (props) => {
  const { name, label, value, data, onChange } = props;

  const [visible, setVisible] = useState<boolean>();
  const ref = useRef<HTMLDivElement>();
  const [checkValue, setCheckValue] = useState<string[]>(value);

  useEffect(() => {
    const handleBodyClick = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!ref.current.contains(target)) {
        setVisible(false);
        return;
      }
    };
    document.body.addEventListener('click', handleBodyClick);

    return () => document.body.removeEventListener('click', handleBodyClick);
  }, []);

  const handleConfirm = () => {
    setVisible(false);
    onChange(name, checkValue);
  };

  return (
    <div ref={ref} className={`filter-container ${value?.length ? 'filtered' : ''}`}>
      <div className="text-wrapper" onClick={() => setVisible(true)}>
        <svg>
          <use href={`${filter}#filter`} />
        </svg>
        {label}
      </div>

      {visible && (
        <div className="dropdown">
          <CheckboxGroup
            checkAll
            data={data}
            value={checkValue}
            onChange={(_: unknown, value: string[]) => setCheckValue(value)}
          />
          <div className="footer">
            <span role="button" onClick={() => setVisible(false)}>
              取消
            </span>
            <span className="confirm" role="button" onClick={handleConfirm}>
              确认
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export interface TableProps extends SPATableProps {
  topContent?: ReactNode;
  headers: (ColumnProps & { filterSource?: FilterProps['data']; tooltip?: string })[];
  searchableCols?: string[];
  selectableCol?: string;
  selected?: string[];
  // 是否默认全选
  selectAll?: boolean;
  canOptional?: ColumnProps['canOptional'];
  onSelected?: (value: string[]) => void;
  tips?: ReactNode;
}

const worker = new Worker('./worker.js');

const Table: React.FC<TableProps> = (props) => {
  const {
    topContent,
    headers,
    searchableCols,
    data,
    striped,
    selectableCol,
    selectAll,
    selected,
    canOptional,
    onSelected,
    tips,
  } = props;
  const [loading, setLoading] = useState<boolean>();
  const [searchValue, setSearchValue] = useState<string>('');
  const [lastSearch, setLastSearch] = useState<string>('');
  const [visibleData, setVisibleData] = useState<any[]>([]);
  const [filterName, setFilterName] = useState<string>();
  const [filterValue, setFilterValue] = useState<string[]>();
  const [page, setPage] = useState<number>(1);
  const containerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (selectAll) {
      onSelected(data?.map((item: any) => item[selectableCol]));
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, []);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      setVisibleData(e.data[1]);
      setPage(1);
      setLoading(false);
    };
    worker.addEventListener('message', handleMessage);

    return () => worker.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!filterValue?.length || !filterName) {
      setVisibleData(data);
      return;
    }

    setLoading(true);
    worker.postMessage(['filter', [data, filterName, filterValue]]);
  }, [data, filterName, filterValue]);

  const handleSearch = () => {
    if (!searchValue.trim() || searchValue.trim() === lastSearch) {
      return;
    }

    setLoading(true);
    setLastSearch(searchValue.trim());
    setFilterName(null);
    setFilterValue(null);
    worker.postMessage(['search', [data, searchableCols, searchValue]]);
  };

  const handleFilter = (name: string, value: string[]) => {
    setFilterName(value?.length ? name : null);
    setFilterValue(value);
    setSearchValue('');
  };

  const renderTh = (header: TableProps['headers'][number]) => {
    if (header.filterable) {
      return (
        <Filter
          name={header.name}
          label={header.label}
          value={filterValue}
          data={header.filterSource}
          onChange={handleFilter}
        />
      );
    }

    if (header.tooltip) {
      return (
        <Tooltip popover={header.tooltip} type="question-filled">
          {header.label}
        </Tooltip>
      );
    }

    return header.label;
  };

  const realData = visibleData.slice((page - 1) * 10, page * 10);

  return (
    <div className="table-container" ref={containerRef}>
      <div className="top">
        {topContent}
        {searchableCols?.length > 0 && (
          <div className="input-container">
            <Icon name="search-outlined" />
            <input
              className="spaui-input"
              value={searchValue}
              placeholder="请输入账户 ID 或关键词，多个关键词请用空格隔开"
              onChange={(e) => {
                setSearchValue(e.target.value);
                if (!e.target.value) {
                  setVisibleData(data);
                  setLastSearch(null);
                }
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              onBlur={handleSearch}
            />
            {searchValue && (
              <Icon
                name="close-filled"
                color="#aaafba"
                onClick={() => {
                  setSearchValue('');
                  setVisibleData(data);
                }}
              />
            )}
          </div>
        )}
      </div>

      <div className="tips">{tips}</div>

      <div className="wrapper">
        <SPATable
          {...props}
          striped={striped}
          loading={loading || props.loading}
          data={realData}
          loadingTrClassName="loading-tr"
          LoadingComponent={() => (
            <div className="loading-wrapper">
              <div className="loading" />
            </div>
          )}
          bottomSpaceElement={
            props.bottomSpaceElement || (
              <div style={{ height: realData.length < 10 ? `${(10 - realData.length) * 37}px` : 0 }} />
            )
          }
        >
          {selectableCol && (
            <Column
              target={selectableCol}
              type="optional"
              multiple
              value={selected}
              width={32}
              onOptionalChange={(_: unknown, value: string[]) => onSelected(value)}
              canOptional={canOptional}
            />
          )}
          {headers.map((header) => (
            /* 手动重置 filterable 属性 */
            <Column key={header.name} {...header} filterable={false}>
              {renderTh(header)}
            </Column>
          ))}
        </SPATable>

        {visibleData.length > 10 && (
          <div className="bottom">
            <span>共{visibleData.length}条记录，10条/页</span>
            <PageBar
              value={page}
              pageSize={10}
              totalSize={visibleData.length}
              showPageInfo={false}
              showTotalSize={false}
              showTotalPage={false}
              showPageJudge={false}
              onChange={(_: unknown, value: number) => setPage(value)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

Table.defaultProps = {
  data: [],
  striped: true,
};

export default Table;

const div = document.createElement('div');
div.style.position = 'absolute';
div.style.top = '-100vh';
div.style.left = '-100vw';
div.style.overflow = 'auto';
div.style.whiteSpace = 'nowrap';
document.body.appendChild(div);

const isEllipsis = (text: string, width: number) => {
  div.style.width = `${width}px`;
  div.textContent = text;

  return div.offsetWidth < div.scrollWidth;
};

export const renderEllipsis =
  // eslint-disable-next-line react/display-name
  (width: number, renderFn?: (data: any) => string) => (type: string, name: string, value: string, data?: any) => {
    if (type === 'data') {
      const text = renderFn ? renderFn(data) : value;
      if (!isEllipsis(text, width)) {
        return text;
      }

      return (
        <Tooltip popover={text} type={null}>
          <div className="ellipsis" style={{ maxWidth: width }}>
            {text}
          </div>
        </Tooltip>
      );
    }
    return value;
  };
